import { Coordinate, EntityLayerType, SearchEntityVersionsByLocationFragment } from 'types/graphql'

import { SelectValue } from 'src/components/Selects/EntityVersionSearch/types'
import { selectValueEquals } from 'src/components/Selects/EntityVersionSearch/util'
import { MapShape } from 'src/components/shared/ArcGisMap/types'
import { filterNil } from 'src/utils'
import { getShowPinForEntityMapShape } from 'src/utils/entityDisplayHelpers'

type BuildEntityVersionSearchMapShapesArgs = {
  entityVersions: SearchEntityVersionsByLocationFragment[]
  selectedValues: SelectValue[]
  selectedLayerSingularLabel: string
  defaultEntityLayerType: EntityLayerType
  onAddEntityVersion: (entityVersion: SearchEntityVersionsByLocationFragment) => void
  onRemoveEntityVersion: (selectValue: SelectValue) => void
}

/** Builds ArcGIS map shapes for entity versions shown in the EV field map picker. */
export const buildEntityVersionSearchMapShapes = ({
  entityVersions,
  selectedValues,
  selectedLayerSingularLabel,
  defaultEntityLayerType,
  onAddEntityVersion,
  onRemoveEntityVersion,
}: BuildEntityVersionSearchMapShapesArgs): MapShape[] =>
  filterNil(
    entityVersions.map((entityVersion) => {
      const coordinates: Coordinate[] = filterNil(
        (entityVersion.coordinates ?? []).map((coordinate) =>
          coordinate?.latitude != null && coordinate?.longitude != null
            ? {
                latitude: coordinate.latitude,
                longitude: coordinate.longitude,
              }
            : null
        )
      )

      if (!coordinates.length) {
        return null
      }

      const entityLayerType = entityVersion.entity?.layer?.type ?? defaultEntityLayerType

      const isSelected = selectedValues.some((selectedValue) =>
        selectValueEquals(selectedValue, entityVersion)
      )

      const showPin = getShowPinForEntityMapShape({
        entityLayerType,
        coordinateCount: coordinates.length,
      })

      return {
        description: '',
        shape: {
          coordinates,
          type: 'coordinates' as const,
        },
        shapeKey: `${entityVersion.id}`,
        showPin,
        title: entityVersion.entity?.name ?? '',
        isSelected,
        actions: [
          isSelected
            ? {
                icon: 'x-circle' as const,
                key: 'remove',
                onClick: () => onRemoveEntityVersion(entityVersion),
                title: `Remove ${selectedLayerSingularLabel}`,
              }
            : {
                icon: 'plus-circle' as const,
                key: 'add',
                onClick: () => onAddEntityVersion(entityVersion),
                title: `Select ${selectedLayerSingularLabel}`,
              },
        ],
      }
    })
  )
